import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Avatar,
  HStack,
  Image,
  Select,
  Spinner,
  IconButton,
  Portal,
} from "@chakra-ui/react";
import DeleteIcon from "@root/assets/icons/delete-02.svg";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import CheckMark from "@root/assets/icons/checkMark.svg";
import { DataTable } from "@root/components/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { useJournalistContext } from "@root/context/journalist-context";
import { formatDateToISO, formatDate } from "@root/utils/date-helper";
import SearchInput from "../common/SearchInput";
import { ModalDelete } from "@root/components/modal-delete";
import { OrganizationService } from "@root/services";
import { PAGINATION_DEFAULTS, USER_TYPES } from "@root/constants";
import CustomSelect from "@root/components/common/CustomSelect";
import { MemberDetailsModal } from "../members-listing/MemberDetailsDrawer";
import { ChangeHistoryIcon } from "@root/assets/icons/historyIcon";
import { useGlobalContext } from "@root/context/global-context";
import { ProfileChangeHistoryDrawer } from "../common/drawer/ProfileChangeHistoryDrawer";
import { usePledgeJournalistListTourGuide } from "@root/hooks/usePledgeJournalistListTourGuide";
import { TourGuide, RestartTourButton } from "@root/components/tour-guide";

const PledgeJournalistList = () => {
  const { t } = useTranslation("common");
  const { pledgeJournalistListTourGuide, isMobile: isMobileTour } =
    usePledgeJournalistListTourGuide();
  const {
    aLlJournalistsData,
    fetchALlJournalists,
    aLlJournalistsLoading,
    deleteJournalistManagement,
  } = useJournalistContext();
  const { getChatHistory, userHistory, historyPage } = useGlobalContext();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const [searchValue, setSearchValue] = useState("");
  const [isViewHistoryOpen, setIsViewHistoryOpen] = useState(false);
  const [isChangeHistoryOpen, setIsChangeHistoryOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [organizationOptions, setOrganizationOptions] = useState<
    { id: string; companyName: string }[]
  >([]);
  const [organization, setOrganization] = useState("");
  const [page, setPage] = useState(1);
  const [deletingJournalistId, setDeletingJournalistId] = useState<
    string | number | null
  >(null);
  const fetchJournalistsWithPagination = useCallback(
    (newPage: number = 1, search: string = "", org: string = "") => {
      const payload = {
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        offset: (newPage - 1) * PAGINATION_DEFAULTS.PAGESIZE,
        search: search.trim(),
        subscriberId: org,
      };

      fetchALlJournalists(payload);
    },
    [fetchALlJournalists],
  );

  const handleChangeHistoryClick = async (id: any) => {
    setSelectedUserId(id); // Store the ID
    const payload = {
      id: id,
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      offset: (historyPage - 1) * PAGINATION_DEFAULTS.PAGESIZE,
    };
    try {
      await getChatHistory(payload);
      setIsViewHistoryOpen(true);
    } catch (error) {
      // Error fetching user history
    }
  };

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const orgService = new OrganizationService();
        const response = await orgService.fetchPublicCompanyNames({
          role: USER_TYPES.BOTH_SUBSCRIBER_AND_NON_SUBSCRIBER,
        });
        setOrganizationOptions(response?.data?.data || []);
      } catch (e) {
        setOrganizationOptions([]);
      }
    };
    fetchOrganizations();
  }, []);

  useEffect(() => {
    const payload = {
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      offset: (page - 1) * PAGINATION_DEFAULTS.PAGESIZE,
      search: searchValue.trim(),
      subscriberId: organization || "",
    };
    fetchALlJournalists(payload);
  }, [page, searchValue, organization]);

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setPage(1);
    fetchJournalistsWithPagination(1, value, organization);
  }, 500);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleOrganizationChange = useCallback(
    (value: string) => {
      setOrganization(value);
      setPage(1);
      fetchJournalistsWithPagination(1, searchValue, value);
    },
    [fetchJournalistsWithPagination, searchValue],
  );
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      fetchJournalistsWithPagination(newPage, searchValue, organization);
    },
    [fetchJournalistsWithPagination, searchValue, organization],
  );

  const handleRowClick = (member: any) => {
    setSelectedMember({ ...member, id: member.id });
    setDrawerOpen(true);
  };

  const filteredJournalists = useMemo(() => {
    return aLlJournalistsData?.rows || [];
  }, [aLlJournalistsData?.rows]);

  const columns = useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        header: t("journalistRequests.name"),
        accessorKey: "name",
        cell: ({ row }) => (
          <HStack>
            <Avatar
              size="sm"
              name={row?.original?.fullName}
              src={row?.original?.avatar}
            />
            <Box>
              <Text fontWeight="500">{row?.original?.fullName}</Text>
              <Text fontSize="sm" color="gray.500">
                {row?.original?.phone}
              </Text>
            </Box>
          </HStack>
        ),
      },
      {
        header: t("journalistRequests.email"),
        accessorKey: "email",
        cell: ({ row }) => row?.original?.email,
      },
      {
        header: t("journalistRequests.requestDate"),
        accessorKey: "date",
        cell: ({ row }) => formatDateToISO(row?.original?.createdAt),
      },
      {
        header: t("journalistRequests.lastLogin"),
        accessorKey: "date",
        cell: ({ row }) =>
          formatDate(row?.original?.updatedAt, "YYYY-MM-DD hh:mm A"),
      },
      {
        header: t("journalistRequests.viewHistory"),
        accessorKey: "viewHistory",
        cell: ({ row }) => (
          <Flex w="50%" h="100%" justify="center" align="center">
            <IconButton
              aria-label="View History"
              icon={<ChangeHistoryIcon />}
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleChangeHistoryClick(row?.original?.id);
              }}
            />
          </Flex>
        ),
      },

      {
        header: t("journalistRequests.actions"),
        id: "actions",
        cell: ({ row }) => {
          const isDeleting = deletingJournalistId === row?.original?.id;
          return (
            <Flex justifyContent="flex-start" alignItems="center">
              {isDeleting ? (
                <Spinner size="sm" color="#1819B4" />
              ) : (
                <IconButton
                  aria-label="Delete journalist"
                  icon={<Image src={DeleteIcon} width={18} />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteModal({ isOpen: true, id: row?.original?.id });
                  }}
                />
              )}
            </Flex>
          );
        },
      },
    ],
    [t, deletingJournalistId],
  );

  const noRequestFound = () => (
    <Flex
      flexDir={"column"}
      justifyContent={"center"}
      alignItems={"center"}
      height={"350px"}
    >
      <Image src={CheckMark} />
      <Text fontSize={"24px"} fontWeight={600} mt={3}>
        {t("journalistRequests.noRecentRequests")}
      </Text>
    </Flex>
  );

  return (
    <Flex direction="column" height="100%" py={4} px={8}>
      <Flex justifyContent={"space-between"} mb={4} mt={"16px"}>
        <Box>
          <Text fontSize={"24px"} fontWeight={600} color={"#2F2F34"}>
            {t("AdminSidebar.journalist")}
          </Text>
          <Text mt={"8px"} fontSize={"16px"} fontWeight={400} color={"#798692"}>
            {t("journalistRequests.journalistListDescription")}
          </Text>
        </Box>
      </Flex>
      <Flex mb={6} gap={4}>
        <SearchInput
          data-tour-item="pledge-journalist-list-search"
          maxWidth="600px"
          width={"500px"}
          value={searchValue}
          placeholder={t("journalistRequests.searchBy")}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </Flex>

      {!isMobileTour && !pledgeJournalistListTourGuide.isActive && (
        <Portal>
          <RestartTourButton
            onClick={pledgeJournalistListTourGuide.startTour}
            position={{ bottom: "24px", right: "24px" }}
          />
        </Portal>
      )}
      {!isMobileTour &&
        pledgeJournalistListTourGuide.isActive &&
        pledgeJournalistListTourGuide.currentStepData && (
          <TourGuide
            isActive={pledgeJournalistListTourGuide.isActive}
            currentStep={pledgeJournalistListTourGuide.currentStepData}
            currentStepIndex={pledgeJournalistListTourGuide.currentStep}
            totalSteps={pledgeJournalistListTourGuide.totalSteps}
            onNext={pledgeJournalistListTourGuide.nextStep}
            onBack={pledgeJournalistListTourGuide.previousStep}
            onClose={pledgeJournalistListTourGuide.stopTour}
            targetSelector={
              pledgeJournalistListTourGuide.currentStepData.targetSelector
            }
            borderRadius="8px"
          />
        )}
      <ProfileChangeHistoryDrawer
        isOpen={isViewHistoryOpen}
        onClose={() => {
          setIsViewHistoryOpen(false);
          setSelectedUserId(null); // Clear the ID when closing
        }}
        viewHistoryChange={handleChangeHistoryClick}
        userId={selectedUserId} // Pass the ID to the drawer
      />

      <Box
        maxH={"calc(100vh - 150px)"}
        overflowY={"auto"}
        data-tour-item="pledge-journalist-list-table"
      >
        {aLlJournalistsLoading ? (
          <Flex
            flexDir={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            height={"350px"}
            gap={3}
          >
            <Spinner size="lg" color="#1819B4" />
          </Flex>
        ) : filteredJournalists?.length > 0 ? (
          <DataTable
            data={filteredJournalists}
            columns={columns}
            canEditRow={false}
            getRowId={(row: any) => row?.email}
            showPagination={true}
            page={page}
            pageCount={aLlJournalistsData?.count || 0}
            pageSize={PAGINATION_DEFAULTS.PAGESIZE}
            handlePageChange={handlePageChange}
            onRowClick={handleRowClick}
          />
        ) : (
          noRequestFound()
        )}
      </Box>
      <ModalDelete
        isOpen={deleteModal.isOpen}
        onClose={() => {
          if (!deletingJournalistId) {
            setDeleteModal({ isOpen: false, id: null });
          }
        }}
        onConfirm={async () => {
          if (!deleteModal.id) return;

          setDeletingJournalistId(deleteModal.id);
          setDeleteModal({ isOpen: false, id: null });

          try {
            // Calculate if we're deleting the last item on current page
            const currentPageItemCount = filteredJournalists.length;
            const isLastItemOnPage = currentPageItemCount === 1;
            const shouldGoToPreviousPage = isLastItemOnPage && page > 1;

            // Determine the correct page after deletion
            const newPage = shouldGoToPreviousPage ? page - 1 : page;

            // Update page if needed
            if (shouldGoToPreviousPage) {
              setPage(newPage);
            }

            const payload = {
              limit: PAGINATION_DEFAULTS.PAGESIZE,
              offset: (newPage - 1) * PAGINATION_DEFAULTS.PAGESIZE,
              search: searchValue.trim(),
              subscriberId: organization || "",
            };

            await deleteJournalistManagement(deleteModal.id, payload);
          } finally {
            setDeletingJournalistId(null);
          }
        }}
        title={t("journalistRequests.deleteTitle")}
        description={t("journalistRequests.deleteDescription")}
        deleteButtonTitle={t("journalistRequests.delete")}
        cancelButtonTitle={t("journalistRequests.cancel")}
      />
      <MemberDetailsModal
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        member={selectedMember}
      />
    </Flex>
  );
};

export default PledgeJournalistList;
